import { useState } from 'react'
import { format } from 'date-fns'
import { Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { DateOverride, DateOverrideKind } from '@/types/availability'

interface DateOverrideCalendarProps {
  overrides: DateOverride[]
  onChange: (overrides: DateOverride[]) => void
}

function newOverrideId(): string {
  return `ov-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function formatOverride(ov: DateOverride): string {
  const prefix = ov.kind === 'block' ? 'Blocked' : 'Extra hours'
  if (ov.start_time && ov.end_time) {
    return `${prefix}: ${ov.start_time}–${ov.end_time}`
  }
  return `${prefix} (all day)`
}

export function DateOverrideCalendar({ overrides, onChange }: DateOverrideCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)

  const selectedKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null
  const selectedOverrides = selectedKey ? overrides.filter((o) => o.date === selectedKey) : []
  const modifiers = {
    hasOverride: overrides.map((o) => new Date(`${o.date}T00:00:00`)),
  }

  function addOverride(kind: DateOverrideKind) {
    if (!selectedKey) return
    const newOv: DateOverride = {
      id: newOverrideId(),
      date: selectedKey,
      kind,
      start_time: kind === 'extra' ? '09:00' : null,
      end_time: kind === 'extra' ? '17:00' : null,
    }
    onChange([...overrides, newOv])
  }

  function updateOverride(id: string, patch: Partial<DateOverride>) {
    onChange(overrides.map((o) => (o.id === id ? { ...o, ...patch } : o)))
  }

  function removeOverride(id: string) {
    onChange(overrides.filter((o) => o.id !== id))
  }

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <Calendar
        mode="single"
        selected={selectedDate}
        onSelect={setSelectedDate}
        modifiers={modifiers}
        modifiersClassNames={{ hasOverride: 'ring-2 ring-primary-500 ring-offset-1' }}
      />

      <div className="space-y-3">
        {!selectedDate ? (
          <p className="text-sm text-slate-500">Select a date to add overrides.</p>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-slate-900">
                {format(selectedDate, 'EEEE, MMMM d, yyyy')}
              </h3>
              <div className="flex gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => addOverride('block')}>
                  Block
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => addOverride('extra')}>
                  Extra hours
                </Button>
              </div>
            </div>

            {selectedOverrides.length === 0 ? (
              <p className="text-sm text-slate-500">No overrides for this date.</p>
            ) : (
              <div className="space-y-2">
                {selectedOverrides.map((ov) => (
                  <div
                    key={ov.id}
                    className="flex items-center gap-3 rounded-md border border-slate-200 p-3"
                  >
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Select
                          value={ov.kind}
                          onValueChange={(value) =>
                            updateOverride(ov.id, { kind: value as DateOverrideKind })
                          }
                        >
                          <SelectTrigger className="w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="block">Block</SelectItem>
                            <SelectItem value="extra">Extra hours</SelectItem>
                          </SelectContent>
                        </Select>
                        <span className="text-xs text-slate-500">{formatOverride(ov)}</span>
                      </div>

                      {ov.kind === 'extra' && (
                        <div className="flex items-center gap-2">
                          <Label htmlFor={`ov-start-${ov.id}`} className="sr-only">
                            Start
                          </Label>
                          <Input
                            id={`ov-start-${ov.id}`}
                            type="time"
                            value={ov.start_time ?? ''}
                            onChange={(e) => updateOverride(ov.id, { start_time: e.target.value })}
                            className="w-32"
                          />
                          <span className="text-slate-400">–</span>
                          <Label htmlFor={`ov-end-${ov.id}`} className="sr-only">
                            End
                          </Label>
                          <Input
                            id={`ov-end-${ov.id}`}
                            type="time"
                            value={ov.end_time ?? ''}
                            onChange={(e) => updateOverride(ov.id, { end_time: e.target.value })}
                            className="w-32"
                          />
                        </div>
                      )}

                      {ov.kind === 'block' && (
                        <div className="flex items-center gap-2">
                          <Label htmlFor={`ov-block-start-${ov.id}`} className="text-xs">
                            Optional range
                          </Label>
                          <Input
                            id={`ov-block-start-${ov.id}`}
                            type="time"
                            value={ov.start_time ?? ''}
                            onChange={(e) =>
                              updateOverride(ov.id, { start_time: e.target.value || null })
                            }
                            className="w-32"
                          />
                          <span className="text-slate-400">–</span>
                          <Input
                            type="time"
                            value={ov.end_time ?? ''}
                            onChange={(e) =>
                              updateOverride(ov.id, { end_time: e.target.value || null })
                            }
                            className="w-32"
                          />
                        </div>
                      )}
                    </div>

                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => removeOverride(ov.id)}
                      aria-label="Remove override"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
